import { Apartment } from "@boark/shared/types/apartment";
import { PersonWithHoldingsAndLeasesAndSubletsAndApartment } from "@boark/shared/types/person-with-holdings-and-leases-and-sublets-and-apartment";
import { getResidencyWithinDate } from "@boark/shared/utils/apartment-residency";
import { Outlet, useSearchParams } from "react-router-dom";

import { ApartmentLink } from "../../components/apartment-link";
import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { Tooltip } from "../../components/tooltip";
import { useGetPersons } from "../../queries/person";
import { formatPersonLabel } from "../../services/person";
import { useLocale } from "../../stores/locale";
import { getAgeFromPersonalIdentificationNumber } from "../../utils/age";
import { createIssueRouteForRecord } from "../../utils/create-issue-route";
import { recordIdToId } from "../../utils/record-id-to-id";

type PersonWithApartmentData =
  PersonWithHoldingsAndLeasesAndSubletsAndApartment & {
    status: "owner" | "tenant" | "subtenant" | "none";
    currentApartment?: Omit<Apartment, "holdings" | "leases">;
    age?: number;
  };

export function Persons() {
  const { locale: t, get: getLocale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: persons, isLoading: isPersonsLoading } = useGetPersons();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.person.persons}</H1>
          <Button route="/admin/register/person/create">
            {t.person.create}
          </Button>
        </div>
        <Table
          actions={(row: PersonWithApartmentData) => [
            {
              label: t.issue.create,
              route: createIssueRouteForRecord({
                type: "person",
                recordId: recordIdToId(row.id),
                label: formatPersonLabel(row),
              }),
            },
          ]}
          data={(persons ?? []).map(getPersonData)}
          loading={isPersonsLoading}
          editRouteButton={(row) =>
            `/admin/register/person/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row) =>
            `/admin/register/person/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "apartment.index"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({
              filterBy: JSON.stringify(value),
            });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="firstName">{t.person.firstName}</Column>
          <Column field="lastName">{t.person.lastName}</Column>
          <Column
            body={(row: PersonWithApartmentData) =>
              t.person.status[row.status]
            }
            field="status"
            filterableBy="select"
            filterOptions={() => [
              { label: t.person.status.none, value: "none" },
              { label: t.person.status.owner, value: "owner" },
              { label: t.person.status.tenant, value: "tenant" },
              { label: t.person.status.subtenant, value: "subtenant" },
            ]}
          >
            {t.person.status.status}
          </Column>
          <Column
            body={(row: PersonWithApartmentData) =>
              row.currentApartment ? (
                <ApartmentLink apartment={row.currentApartment} />
              ) : (
                ""
              )
            }
            field="currentApartment.index"
          >
            {getLocale("person.currentApartment")}
          </Column>
          <Column field="personalIdentificationNumber">
            <Tooltip content={t.person.personalIdentificationNumber}>
              {t.person.personalIdentificationNumberAbbreviation}
            </Tooltip>
          </Column>
          <Column field="age">{t.person.age}</Column>
          <Column field="email">{t.person.email}</Column>
          <Column field="phone">{t.person.phone}</Column>
          <Column field="issueCount">{t.person.issueCount}</Column>
        </Table>
      </div>
    </>
  );
}

function getPersonData(
  person: PersonWithHoldingsAndLeasesAndSubletsAndApartment,
): PersonWithApartmentData {
  let currentApartment:
    | PersonWithApartmentData["currentApartment"]
    | undefined = undefined;
  let status: PersonWithApartmentData["status"] = "none";

  const currentHolding = getResidencyWithinDate(person.holdings, new Date());
  const currentLease = getResidencyWithinDate(person.leases, new Date());
  const currentSublet = getResidencyWithinDate(person?.sublets, new Date());

  if (currentHolding) {
    currentApartment = currentHolding.apartment;
    status = "owner";
  } else if (currentLease) {
    currentApartment = currentLease.apartment;
    status = "tenant";
  } else if (currentSublet) {
    if ("holding" in currentSublet) {
      currentApartment = currentSublet.holding.apartment;
    } else if ("lease" in currentSublet) {
      currentApartment = currentSublet.lease.apartment;
    }
    status = "subtenant";
  }

  return {
    ...person,
    currentApartment,
    status,
    age: getAgeFromPersonalIdentificationNumber(
      person.personalIdentificationNumber,
    ),
  };
}
